export const C3_CUI_DATA_SESSION_STORAGE_KEY = "cui-data";

const C3_CUI_DATA_KEYS = {
  ACCESS_TOKEN: "access_token",
  EMAIL: "email",
  ENV: "env"
};

export const C3_CUI_DATA_STATUS = {
  INACTIVE: "inactive",
  READY: "ready"
};

/**
 * Checks whether a value is a non-array object that can represent parsed CUI data.
 *
 * @param {*} value Value to test.
 * @returns {boolean} True when the value is a plain object-like value.
 */
const isObject = value =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Safely returns the browser window object when running in a browser context.
 *
 * @returns {Window|undefined} Browser window, or undefined during non-browser execution.
 */
const getWindow = () =>
  typeof window === "undefined" ? undefined : window;

/**
 * Safely returns sessionStorage from the supplied window object.
 *
 * @param {Window|undefined} windowObj Window object used to access sessionStorage.
 * @returns {Storage|undefined} sessionStorage when available.
 */
const getSessionStorage = windowObj => {
  try {
    return windowObj?.sessionStorage;
  } catch (error) {
    return undefined;
  }
};

/**
 * Decodes a URI component without throwing on malformed encoded strings.
 *
 * @param {string} value Value to decode.
 * @returns {string} Decoded value, or the original value when decoding fails.
 */
const safeDecodeURIComponent = value => {
  try {
    return decodeURIComponent(value);
  } catch (error) {
    return value;
  }
};

/**
 * Parses JSON without throwing on invalid JSON candidates.
 *
 * @param {string} value JSON string candidate.
 * @returns {*|undefined} Parsed value, or undefined when parsing fails.
 */
const tryParseJson = value => {
  try {
    return JSON.parse(value);
  } catch (error) {
    return undefined;
  }
};

/**
 * Normalizes escaped JSON strings that may come from session storage serialization.
 *
 * @param {string} value Escaped JSON string candidate.
 * @returns {string} Candidate with escaped quotes and slashes normalized.
 */
const normalizeEscapedJson = value =>
  value.replace(/\\"/g, '"').replace(/\\\\/g, "\\");

/**
 * Parses a CUI data value from the supported stored formats.
 *
 * The CUI data may already be an object, normal JSON, URI-encoded JSON, quoted
 * JSON, escaped JSON, or a nested JSON string.
 *
 * @param {*|string} rawCuiDataValue Raw stored CUI data value.
 * @returns {Object|null} Parsed CUI data object, or null when the value is invalid.
 */
export const parseCuiDataValue = rawCuiDataValue => {
  if (isObject(rawCuiDataValue)) {
    return rawCuiDataValue;
  }

  if (typeof rawCuiDataValue !== "string") {
    return null;
  }

  const trimmedValue = rawCuiDataValue.trim();
  if (!trimmedValue) {
    return null;
  }

  const decodedValue = safeDecodeURIComponent(trimmedValue);
  const unwrappedValue = decodedValue.replace(/^['"]|['"]$/g, "");
  const parseCandidates = [
    trimmedValue,
    decodedValue,
    unwrappedValue,
    normalizeEscapedJson(unwrappedValue)
  ];

  for (const candidate of parseCandidates) {
    const parsedCandidate = tryParseJson(candidate);

    if (isObject(parsedCandidate)) {
      return parsedCandidate;
    }

    if (typeof parsedCandidate === "string") {
      const reparsedCandidate = tryParseJson(parsedCandidate);
      if (isObject(reparsedCandidate)) {
        return reparsedCandidate;
      }
    }
  }

  return null;
};

/**
 * Reads and parses CUI data from session storage.
 *
 * @param {Storage|undefined} storage Storage object to read from.
 * @returns {Object|null} Parsed CUI data object, or null when missing/invalid.
 */
export const getStoredCuiData = (storage = getSessionStorage(getWindow())) => {
  const storedValue = storage?.getItem(C3_CUI_DATA_SESSION_STORAGE_KEY);
  return storedValue ? parseCuiDataValue(storedValue) : null;
};

/**
 * Checks whether a required CUI data field is present as a non-empty string.
 *
 * @param {Object|null|undefined} cuiData Parsed CUI data object.
 * @param {string} key Required CUI data key.
 * @returns {boolean} True when the key contains a non-empty string value.
 */
const hasRequiredCuiDataValue = (cuiData, key) =>
  Boolean(cuiData?.[key] && typeof cuiData[key] === "string");

/**
 * Validates that stored CUI data has the fields required by C3 header consumers.
 *
 * @param {Object|null|undefined} cuiData Parsed CUI data object.
 * @returns {boolean} True when access_token, email, and env are non-empty strings.
 */
export const isC3CuiDataEligible = cuiData =>
  Object.values(C3_CUI_DATA_KEYS).every(key =>
    hasRequiredCuiDataValue(cuiData, key)
  );

/**
 * Removes canonical C3 CUI data from session storage.
 *
 * @param {Storage|undefined} storage Storage object to clear.
 * @returns {void}
 */
export const clearC3CuiSessionData = (
  storage = getSessionStorage(getWindow())
) => {
  storage?.removeItem(C3_CUI_DATA_SESSION_STORAGE_KEY);
};

/**
 * Parses and rewrites CUI data into normalized JSON in session storage.
 *
 * @param {*|string} rawCuiDataValue Raw CUI data value.
 * @param {Storage|undefined} storage Storage object to update.
 * @returns {Object|null} Parsed CUI data object, or null when parsing fails.
 */
const storeNormalizedCuiData = (rawCuiDataValue, storage) => {
  const parsedCuiData = parseCuiDataValue(rawCuiDataValue);

  if (!parsedCuiData) {
    return null;
  }

  storage?.setItem(
    C3_CUI_DATA_SESSION_STORAGE_KEY,
    JSON.stringify(parsedCuiData)
  );
  return parsedCuiData;
};

/**
 * Normalizes the existing session storage CUI value and clears it when invalid.
 *
 * @param {Storage|undefined} storage Storage object containing CUI data.
 * @returns {boolean} True when a valid CUI data object remains in storage.
 */
const normalizeStoredCuiData = storage => {
  const storedCuiData = storage?.getItem(C3_CUI_DATA_SESSION_STORAGE_KEY);

  if (!storedCuiData) {
    return false;
  }

  const normalizedCuiData = storeNormalizedCuiData(storedCuiData, storage);
  if (!normalizedCuiData) {
    clearC3CuiSessionData(storage);
    return false;
  }

  if (!isC3CuiDataEligible(normalizedCuiData)) {
    clearC3CuiSessionData(storage);
    return false;
  }

  return true;
};

/**
 * Initializes C3 CUI data state from session storage only.
 *
 * This does not fetch CUI data. The value must already exist in
 * sessionStorage["cui-data"] before the header bootstrap runs.
 *
 * @param {Object} options Bootstrap options.
 * @param {Window|undefined} options.windowObj Window object used to resolve storage.
 * @param {Storage|undefined} options.storage Storage override for tests.
 * @returns {{status: string}} C3 CUI data readiness status.
 */
export const initializeC3CuiDataBootstrap = ({
  windowObj = getWindow(),
  storage = getSessionStorage(windowObj)
} = {}) => {
  if (!windowObj || !storage) {
    return { status: C3_CUI_DATA_STATUS.INACTIVE };
  }

  return {
    status: normalizeStoredCuiData(storage)
      ? C3_CUI_DATA_STATUS.READY
      : C3_CUI_DATA_STATUS.INACTIVE
  };
};
